import React, { useState, useEffect } from 'react';
import { Alert, Button, Typography, Select } from 'antd';
import {
  RocketOutlined,
  CodeOutlined,
  ExperimentOutlined,
  LinkOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Notify } from '../../../services/notificationService';
import claudeAddConnectorMenu from '../../../assets/claude-add-connector-menu.png';
import claudeAddConnector from '../../../assets/claude-add-connector-modal.png';
import codexMcpSettings from '../../../assets/codex-mcp-settings.png';
import {
  mcpClients,
  handleOpenClient as handleOpenClientUtil,
  getClientName,
} from '../../../utils/mcpClientUtils';
import { trackEvent } from '../../../utils/monitoring';
const { Text } = Typography;

interface McpSetupStepProps {
  onClientSelect?: (clientId: string | undefined) => void;
  initialClient?: string;
  hideIntroText?: boolean;
}

export const McpSetupStep: React.FC<McpSetupStepProps> = ({
  onClientSelect,
  initialClient,
  hideIntroText = false,
}) => {
  const [selectedClient, setSelectedClient] = useState<string | undefined>(
    initialClient
  );

  // Update selectedClient when initialClient changes (e.g., when modal reopens)
  useEffect(() => {
    setSelectedClient(initialClient);
  }, [initialClient]);

  const mcpServerUrl =
    process.env.REACT_APP_MCP_SERVER_URL || 'https://mcp.cirra.ai/mcp';

  const copyToClipboard = (text: string, successMessage: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        Notify.success(successMessage);
      })
      .catch(() => {
        Notify.error('Failed to copy to clipboard');
      });
  };

  const CodeValue: React.FC<{
    value: string;
    copyable?: boolean;
  }> = ({ value, copyable: isCopyable = false }) => (
    <Text
      className="walkthrough-copyable-value"
      copyable={
        isCopyable
          ? {
              text: value,
              onCopy: () => copyToClipboard(value, 'Copied!'),
            }
          : false
      }
    >
      {value}
    </Text>
  );

  const handleOpenClient = () => {
    handleOpenClientUtil(selectedClient);
  };

  const handleInstallConnector = () => {
    switch (selectedClient) {
      case 'claude-ai':
        window.open('https://claude.ai/customize/connectors', '_blank');
        break;
      case 'chatgpt':
        window.open('https://chatgpt.com/#settings/Connectors', '_blank');
        break;
      case 'vscode':
        window.open(
          `vscode:mcp/install?%7B%22name%22:%22cirra-ai%22,%22gallery%22:false,%22url%22:%22${mcpServerUrl}%22%7D`,
          '_blank'
        );
        break;
      case 'cursor':
        window.open(
          'https://cursor.com/en/install-mcp?name=cirra-ai&config=eyJ0eXBlIjoiaHR0cCIsInVybCI6Imh0dHBzOi8vbWNwLmNpcnJhLmFpL21jcCJ9',
          '_blank'
        );
        break;
      default:
        handleOpenClient();
    }
  };

  const handleClientChange = (value: string) => {
    setSelectedClient(value);
    onClientSelect?.(value);
    trackEvent('walkthrough_mcp_client_selected', {
      client_id: value,
      client_name: getClientName(value),
    });
  };

  const getClientSpecificInstructions = () => {
    if (!selectedClient) return [];
    switch (selectedClient) {
      case 'claude-ai':
        return [
          {
            title: 'Open Claude integration settings',
            icon: <RocketOutlined />,
            content: (
              <div>
                <p>
                  Navigate to{' '}
                  <a
                    href="https://claude.ai/customize/connectors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://claude.ai/customize/connectors
                  </a>
                </p>
                <Alert
                  message="Claude Pro (or better) Required"
                  description="You need at least a Claude Pro subscription to add custom MCP connectors."
                  type="warning"
                  showIcon
                  style={{ marginTop: 12 }}
                />
              </div>
            ),
          },
          {
            title: 'Add the Cirra AI Connector',
            icon: <LinkOutlined />,
            content: (
              <div>
                <ol style={{ paddingLeft: 20 }}>
                  <li>
                    Click the <strong>+</strong> icon in the{' '}
                    <strong>Connectors</strong> header and then select{' '}
                    <strong>... Add custom connector</strong> from the menu
                  </li>
                  <img
                    src={claudeAddConnectorMenu}
                    alt="Claude Add Connector Menu"
                    className="walkthrough-screenshot"
                    style={{ width: '600px' }}
                  />
                  <li>Fill in the connector details:</li>
                  <img
                    src={claudeAddConnector}
                    alt="Claude Add Connector Modal"
                    className="walkthrough-screenshot"
                    style={{ width: '500px' }}
                  />
                  <li>
                    Double-check the URL:
                    <CodeValue value={mcpServerUrl} copyable />
                  </li>
                  <li>
                    Click <strong>&quot;Add&quot;</strong> to create the
                    connector
                  </li>
                </ol>
              </div>
            ),
          },
          {
            title: 'Connect & Allow Access',
            icon: <ExperimentOutlined />,
            content: (
              <div>
                <p>Complete the connection:</p>
                <ol style={{ paddingLeft: 20 }}>
                  <li>
                    Click the <strong>&quot;Connect&quot;</strong> button to
                    connect to Cirra AI
                  </li>
                  <li>
                    When prompted, click{' '}
                    <strong>&quot;Allow Access&quot;</strong> to authorize the
                    connection
                  </li>
                  <li>If prompted, log in to Cirra AI</li>
                  <li>
                    You should see a confirmation of the successful connection
                  </li>
                </ol>
              </div>
            ),
          },
        ];
      case 'claude-desktop':
        return [
          {
            title: 'Open Claude Desktop',
            icon: <RocketOutlined />,
            content: (
              <div>
                <p>First, let&apos;s open Claude Desktop:</p>
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <Button
                    type="primary"
                    icon={<RocketOutlined />}
                    onClick={handleOpenClient}
                    size="large"
                  >
                    Open Claude Desktop
                  </Button>
                </div>
                <Alert
                  message="Claude Desktop should open"
                  description={
                    <>
                      Claude Desktop should open on your machine if you have it
                      installed.
                      <br />
                      If not click{' '}
                      <a
                        href="https://claude.ai/download"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        here
                      </a>{' '}
                      to download it.
                    </>
                  }
                  type="info"
                  showIcon
                />
              </div>
            ),
          },
          {
            title: 'Add the Cirra AI Connector',
            icon: <SettingOutlined />,
            content: (
              <div>
                <Alert
                  message="Claude Pro Required"
                  description="You need at least a Claude Pro subscription to add custom MCP connectors."
                  type="warning"
                  showIcon
                  style={{ marginTop: 12 }}
                />
                <p>In Claude Desktop:</p>
                <ol style={{ paddingLeft: 20 }}>
                  <li>
                    Open the connectors settings (Customize -&gt; Connectors)
                  </li>
                  <li>
                    Click the <strong>+</strong> icon in the{' '}
                    <strong>Connectors</strong> header and then select{' '}
                    <strong>... Add custom connector</strong> from the menu
                  </li>
                  <img
                    src={claudeAddConnectorMenu}
                    alt="Claude Add Connector Menu"
                    className="walkthrough-screenshot"
                    style={{ width: '600px' }}
                  />
                  <li>Fill in the connector details:</li>
                  <img
                    src={claudeAddConnector}
                    alt="Claude Add Connector Modal"
                    className="walkthrough-screenshot"
                    style={{ width: '500px' }}
                  />
                  <li>
                    Double-check the URL:
                    <CodeValue value={mcpServerUrl} copyable />
                  </li>
                  <li>
                    Click <strong>&quot;Add&quot;</strong> to create the
                    connector
                  </li>
                </ol>
              </div>
            ),
          },
          {
            title: 'Connect & Allow Access',
            icon: <ExperimentOutlined />,
            content: (
              <div>
                <p>Complete the connection:</p>
                <ol style={{ paddingLeft: 20 }}>
                  <li>
                    Click the <strong>&quot;Connect&quot;</strong> button to
                    connect to Cirra AI
                  </li>
                  <li>
                    When prompted, click{' '}
                    <strong>&quot;Allow Access&quot;</strong> to authorize the
                    connection
                  </li>
                  <li>If prompted, log in to Cirra AI</li>
                  <li>
                    You should see a confirmation of the successful connection
                  </li>
                </ol>
                <Alert
                  message="Note"
                  description="You may need to restart Claude Desktop for the connector to appear."
                  type="info"
                  showIcon
                  style={{ marginTop: 12 }}
                />
              </div>
            ),
          },
        ];
      case 'chatgpt':
        return [
          {
            title: 'Open ChatGPT 5 Apps settings',
            icon: <RocketOutlined />,
            content: (
              <div>
                <p>
                  Navigate to the{' '}
                  <a
                    href="https://chatgpt.com/#settings/Connectors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ChatGPT 5 Apps settings
                  </a>
                </p>
                <Alert
                  message="ChatGPT Plus (or better) Required"
                  description="You need at least a ChatGPT Plus subscription to add custom MCP connectors."
                  type="warning"
                  showIcon
                  style={{ marginTop: 12 }}
                />
              </div>
            ),
          },
          {
            title: 'Add the Cirra AI App',
            icon: <LinkOutlined />,
            content: (
              <div>
                <p>Add the Cirra AI MCP app in ChatGPT:</p>
                <ol style={{ paddingLeft: 20 }}>
                  <li>
                    Click <strong>Advanced Settings</strong>
                  </li>
                  <li>
                    Enable <strong>Developer mode</strong>
                  </li>
                  <li>
                    Click <strong>Create app</strong>
                  </li>
                  <li>
                    Enter the following data
                    <ol>
                      <li>
                        <strong>Name:</strong> <CodeValue value="Cirra AI" />
                      </li>
                      <li>
                        <strong>Description:</strong> Use Cirra AI to manage
                        Salesforce -- it can view and create both metadata and
                        data. It can also answer questions about your Cirra AI
                        account and connected Salesforce orgs
                      </li>
                      <li>
                        <strong>Server URL:</strong>{' '}
                        <CodeValue value={mcpServerUrl} copyable />
                      </li>
                      <li>
                        <strong>Authentication:</strong>{' '}
                        <CodeValue value="OAuth" />
                      </li>
                    </ol>
                  </li>
                  <li>
                    Click the checkbox next to &quot;I trust this
                    application&quot;
                  </li>
                  <li>
                    Click <strong>Create</strong> to create the connector
                  </li>
                </ol>
              </div>
            ),
          },
        ];
      case 'openai-codex':
        return [
          {
            title: 'Open OpenAI Codex Settings',
            icon: <RocketOutlined />,
            content: (
              <div>
                <p>
                  Open OpenAI Codex and navigate to{' '}
                  <strong>Settings → MCP servers</strong>.
                </p>
                <Alert
                  message="ChatGPT Plus (or better) Required"
                  description="You need at least a ChatGPT Plus subscription to access OpenAI Codex."
                  type="warning"
                  showIcon
                  style={{ marginTop: 12 }}
                />
              </div>
            ),
          },
          {
            title: 'Add the Cirra AI MCP Server',
            icon: <LinkOutlined />,
            content: (
              <div>
                <ol style={{ paddingLeft: 20 }}>
                  <li>
                    Click <strong>Add MCP server</strong>
                  </li>
                  <li>
                    Select <strong>Streamable HTTP</strong> as the transport
                    type
                  </li>
                  <li>
                    Enter the following details:
                    <ol>
                      <li>
                        <strong>Name:</strong> <CodeValue value="Cirra AI" />
                      </li>
                      <li>
                        <strong>URL:</strong>{' '}
                        <CodeValue value={mcpServerUrl} copyable />
                      </li>
                    </ol>
                  </li>
                  <img
                    src={codexMcpSettings}
                    alt="OpenAI Codex MCP Settings"
                    className="walkthrough-screenshot"
                    style={{ width: '600px' }}
                  />
                  <li>
                    Click <strong>&quot;Save&quot;</strong> to add the server
                  </li>
                </ol>
              </div>
            ),
          },
        ];
      case 'vscode':
        return [
          {
            title: 'Install MCP Server in VS Code',
            icon: <CodeOutlined />,
            content: (
              <div>
                <p>Add the Cirra AI MCP server to VS Code:</p>
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <Button
                    type="primary"
                    icon={<CodeOutlined />}
                    onClick={handleInstallConnector}
                    size="large"
                  >
                    Install in VS Code
                  </Button>
                </div>
                <Alert
                  message="VS Code should open"
                  description={
                    <>
                      VS Code should open on your machine if you have it
                      installed.
                      <br />
                      If not click{' '}
                      <a
                        href="https://code.visualstudio.com/download"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        here
                      </a>{' '}
                      to download it.
                    </>
                  }
                  type="info"
                  showIcon
                />
                <br />
                <Alert
                  message="GitHub Copilot Required"
                  description="You need GitHub Copilot access in VS Code to use MCP servers."
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                />
              </div>
            ),
          },
          {
            title: 'Complete Installation',
            icon: <SettingOutlined />,
            content: (
              <div>
                <ol style={{ paddingLeft: 20 }}>
                  <li>
                    Click <strong>Open Visual Studio Code</strong>
                  </li>
                  <li>
                    Click <strong>Install</strong> in the installation screen
                  </li>
                  <li>
                    If the authentication page does not appear in your browser
                    after a few seconds:,
                    <ol>
                      <li>
                        click <strong>Cancel</strong>
                      </li>
                      <li>
                        Then, in the &quot;URL Handler&quot; dialog that
                        appears, click <strong>Yes</strong>
                      </li>
                    </ol>
                  </li>
                  <li>
                    Click <strong>Open</strong> to open the authorization page
                    in your web browser
                  </li>
                </ol>
                <p>
                  If the steps above fail, follow the instructions for VS Code{' '}
                  <a
                    href="https://docs.cirra.ai/mcp-server/connecting-mcp"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </a>
                  .
                </p>
              </div>
            ),
          },
        ];
      case 'cline':
        return [
          {
            title: 'Add the Cirra AI MCP Server',
            icon: <LinkOutlined />,
            content: (
              <div>
                <p>In the Cline MCP Servers interface:</p>
                <ol style={{ paddingLeft: 20 }}>
                  <li>
                    Go to the <strong>Remote Servers</strong> tab
                  </li>
                  <li>
                    Enter the server URL:
                    <CodeValue value={mcpServerUrl} copyable />
                  </li>
                  <li>
                    Select <strong>Streamable HTTP</strong> as the transport
                    type
                  </li>
                </ol>
              </div>
            ),
          },
          {
            title: 'Authenticate',
            icon: <ExperimentOutlined />,
            content: (
              <div>
                <ol style={{ paddingLeft: 20 }}>
                  <li>
                    You will see an error message with an{' '}
                    <strong>&quot;Authenticate&quot;</strong> button — click it
                  </li>
                  <li>
                    Your browser will open to the Cirra AI authorization page
                  </li>
                  <li>Sign in and grant permission</li>
                  <li>
                    You will be redirected back to Cline automatically and the
                    server will connect
                  </li>
                </ol>
              </div>
            ),
          },
        ];
      case 'cursor':
        return [
          {
            title: 'Install MCP Server in Cursor',
            icon: <CodeOutlined />,
            content: (
              <div>
                <p>Add the Cirra AI MCP Server to Cursor:</p>
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <Button
                    type="primary"
                    icon={<CodeOutlined />}
                    onClick={handleInstallConnector}
                    size="large"
                  >
                    Install in Cursor
                  </Button>
                </div>
                <Alert
                  message="Cursor should open"
                  description={
                    <>
                      Cursor should open on your machine if you have it
                      installed.
                      <br />
                      If not click{' '}
                      <a
                        href="https://cursor.com/downloads"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        here
                      </a>{' '}
                      to download it.
                    </>
                  }
                  type="info"
                  showIcon
                />
                <br />
                <Alert
                  message="Cursor Subscription Required"
                  description="You need a Cursor subscription to use MCP servers."
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                />
                <p>
                  To finish the connection:
                  <ol>
                    <li>Open Cursor settings</li>
                    <li>Go to MCP & Integrations</li>
                    <li>
                      Click <strong>Connect</strong> next to the Cirra AI server
                    </li>
                  </ol>
                </p>
                <p>
                  If this installation fails, follow the instructions for Cursor{' '}
                  <a
                    href="https://docs.cirra.ai/mcp-server/connecting-mcp"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </a>
                </p>
              </div>
            ),
          },
        ];
      case 'mistral':
        return [
          {
            title: 'Open Mistral',
            icon: <RocketOutlined />,
            content: (
              <div>
                <p>Open Mistral to configure MCP integration:</p>
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  <Button
                    type="primary"
                    icon={<RocketOutlined />}
                    onClick={handleOpenClient}
                    size="large"
                  >
                    Open Mistral
                  </Button>
                </div>
                <Alert
                  message="Mistral Setup"
                  description="Configure Mistral with the MCP server URL."
                  type="info"
                  showIcon
                />
              </div>
            ),
          },
          {
            title: 'Configure MCP Connection',
            icon: <LinkOutlined />,
            content: (
              <div>
                <p>Set up the MCP connection in Mistral:</p>
                <ol style={{ paddingLeft: 20 }}>
                  <li>Navigate to Settings → Integrations</li>
                  <li>Select &quot;MCP Servers&quot;</li>
                  <li>Add new server</li>
                  <li>
                    Server URL:
                    <CodeValue value={mcpServerUrl} copyable />
                  </li>
                  <li>Authenticate when prompted</li>
                </ol>
              </div>
            ),
          },
        ];
      default:
        return [
          {
            title: 'Check documentation',
            icon: <CodeOutlined />,
            content: (
              <div>
                <p>
                  Check the documentation{' '}
                  <a
                    href="https://docs.cirra.ai/mcp-server/connecting-mcp"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </a>
                </p>
              </div>
            ),
          },
        ];
    }
  };

  const setupSteps = getClientSpecificInstructions();

  return (
    <div>
      {!hideIntroText && (
        <div style={{ marginBottom: 20 }}>
          <p>
            Now let&apos;s enable the Cirra AI MCP Server in your favorite AI
            client.
          </p>
          <div className="walkthrough-highlight">
            <div className="walkthrough-highlight-title">
              <CodeOutlined /> How it works
            </div>
            <p>
              MCP (Model Context Protocol) allows AI assistants to connect to
              external services. We&apos;ll configure your AI client to connect
              with Cirra AI so it can help you manage your Salesforce org.
            </p>
          </div>
          <p>
            Complete documentation for adding the Cirra AI MCP connector to your
            AI client is available{' '}
            <a
              href="https://docs.cirra.ai/mcp-server/connecting-mcp"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </a>
            .
          </p>
          <p>
            👉 For most AI clients you will need a paid subscription to take
            full advantage of MCP support.
          </p>
        </div>
      )}
      <div className="walkthrough-client-select" style={{ marginBottom: 20 }}>
        <p style={{ marginBottom: 12, fontWeight: 500 }}>
          Select your AI client and follow the setup instructions:
        </p>
        <Select
          value={selectedClient}
          onChange={handleClientChange}
          style={{ width: '100%', maxWidth: 400 }}
          size="large"
          placeholder="Select an AI client"
          options={mcpClients}
          getPopupContainer={(trigger) =>
            (trigger.closest('.walkthrough-overlay') as HTMLElement) ||
            document.body
          }
        />
      </div>

      {selectedClient && (
        <>
          <p>
            Follow the steps below to connect {getClientName(selectedClient)} to
            your Salesforce organization using the Cirra AI MCP Server.
          </p>
          <div>
            {setupSteps.map((step, index) => (
              <div key={index} style={{ marginBottom: 32 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 16,
                  }}
                >
                  <div style={{ marginRight: 8, color: '#1890ff' }}>
                    {step.icon}
                  </div>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                    {'title' in step ? step.title : ''}
                  </h3>
                </div>
                <div style={{ marginLeft: 32 }}>{step.content}</div>
              </div>
            ))}
          </div>
          {selectedClient !== 'claude-ai' &&
            selectedClient !== 'claude-desktop' &&
            selectedClient !== 'cline' && (
              <>
                <p>
                  To complete the connection, you must authorize the MCP client
                  to access Cirra AI:
                </p>
                <ul>
                  <li>
                    When prompted, click{' '}
                    <strong>&quot;Allow Access&quot;</strong> to authorize the
                    connection
                  </li>
                  <li>If prompted, log in to Cirra AI</li>
                  <li>
                    You should be redirected to {getClientName(selectedClient)}{' '}
                    or see a confirmation of the successful connection
                  </li>
                </ul>
              </>
            )}
          <div className="walkthrough-highlight" style={{ marginTop: 24 }}>
            <div className="walkthrough-highlight-title">
              <RocketOutlined /> Explore the Cirra AI Skills Library
            </div>
            <p>
              Install skills from the{' '}
              <a
                href="https://skills.cirra.ai"
                target="_blank"
                rel="noopener noreferrer"
              >
                Skills Library
              </a>{' '}
              to supercharge {getClientName(selectedClient)} with enhanced
              Salesforce admin capabilities
            </p>
          </div>
        </>
      )}
    </div>
  );
};
